import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Notification from '../../components/common/Notification/Notification';
import ResultsTable from '../../components/results/ResultsTable/ResultsTable';
import HeroBar from '../../components/results/HeroBar/HeroBar';
import ResultsFilter from '../../components/results/ResultsFilter/ResultsFilter';
import ActiveFilterPills from '../../components/results/ActiveFilterPills/ActiveFilterPills';
import { UpgradeModal } from '../../components/subscriptions/UpgradeModal/UpgradeModal';
import EmailVerificationButton from '../../components/results/EmailVerificationButton/EmailVerificationButton';
import EmailVerificationModal from '../../components/results/EmailVerificationModal/EmailVerificationModal';
import BulkDMUnlockModal from '../../components/results/BulkDMUnlockModal/BulkDMUnlockModal';
import ExportEmailConfidenceModal from '../../components/results/ExportModal/ExportEmailConfidenceModal';
import TopProgressBar from '../../components/common/TopProgressBar/TopProgressBar';
import { useSearch } from '../../hooks/useSearch';
import { useResults } from '../../hooks/useResults';
import { useAuth } from '../../hooks/useAuth';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { ResultsFilter as FilterType, DiscoverSummary } from '../../types/results.types';
import { SlidersHorizontal, X } from 'lucide-react';
import { AccessInfo, ExportSelection, getUserSummary, getIndustries } from '../../api/results';
import { BalanceResponse } from '../../types/balance.types';
import { balanceApi } from '../../api/balance';
import { subscriptionsApi } from '../../api/subscriptions';
import { assertReadinessConsistency } from '../../state/listState';
import { isVerifiable } from '../../utils/emailVerifiable';
import { findableCount, findablePeopleIds } from '../../utils/rowActions';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import {
  resetUnlock,
  selectIsBulkDMPolling,
  selectActiveBulkBatch,
  selectHeadCompletionForGlobalUi,
  clearRecentCompletion,
} from '../../store/slices/dmUnlockSlice';
import { clearCompletedVerification } from '../../store/slices/emailVerificationSlice';
import { fetchResults as fetchResultsThunk } from '../../store/slices/resultsSlice';
import EnrichmentCompletionBanner from '../../components/results/shared/EnrichmentCompletionBanner';
import VerificationCompletionBanner from '../../components/results/shared/VerificationCompletionBanner';
import { usePostHog } from 'posthog-js/react';
import type { Scope } from '../../components/results/ScopeToggle/ScopeToggle';

const hasActiveFilters = (filters: FilterType): boolean =>
  Object.values(filters).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && typeof value !== 'undefined' && value !== '';
  });

const countActiveFilters = (filters: FilterType): number =>
  Object.values(filters).filter((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && typeof value !== 'undefined' && value !== '';
  }).length;

const normalizeSmartFilterParams = (filters: FilterType, hasSmartFilters: boolean): FilterType => {
  if (hasSmartFilters) return filters;
  if (!('strongMatchesOnly' in filters) && !('matchedOffering' in filters)) {
    return filters;
  }
  const safeFilters = { ...filters };
  delete safeFilters.strongMatchesOnly;
  delete safeFilters.matchedOffering;
  return safeFilters;
};

const Results: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const searchId = parseInt(id || '0', 10);
  
  const [availableIndustries, setAvailableIndustries] = useState<string[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalContext, setUpgradeModalContext] = useState<'results_lock' | 'dm_unlock_exhausted'>('results_lock');
  const [isLockedPageAccess, setIsLockedPageAccess] = useState(false);
  const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('discover');
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [companyDrawerCloseSignal, setCompanyDrawerCloseSignal] = useState(0);
  const [, setIsCompanyDrawerOpen] = useState(false);
  const [isDMUnlockModalOpen, setIsDMUnlockModalOpen] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [userDiscoverSummary, setUserDiscoverSummary] = useState<DiscoverSummary | null>(null);
  const [userDiscoverSummaryLoading, setUserDiscoverSummaryLoading] = useState(false);
  const [, setHasSuccessfulEnrichment] = useState(false);
  const { isAuthenticated } = useAuth();
  const { hasDMUnlockAccess } = useFeatureAccess();
  const hasInitialized = useRef(false);
  const dispatch = useAppDispatch();
  const isDMUnlockPolling = useAppSelector(selectIsBulkDMPolling);
  const activeBulkBatch = useAppSelector(selectActiveBulkBatch);
  const recentCompletionsCount = useAppSelector((state) => state.dmUnlock.recentCompletions.length);
  const headEnrichmentCompletion = useAppSelector(selectHeadCompletionForGlobalUi);
  const completedVerification = useAppSelector((state) => state.emailVerification.completedVerification);
  const [isSwitchingResultsPage, setIsSwitchingResultsPage] = useState(false);
  const latestRequestedPageRef = useRef<number | null>(null);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [hasExportedThisSearch, setHasExportedThisSearch] = useState(false);
  const [verifyOpenScope, setVerifyOpenScope] = useState<Scope>('page');
  const [enrichOpenScope, setEnrichOpenScope] = useState<Scope>('page');
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const resultsLimitViewedRef = useRef<Set<string>>(new Set());
  const completedSearchViewedRef = useRef<Set<number>>(new Set());
  const posthog = usePostHog();
  const coachmarkKey = `results-enrich-coachmark:v1:search:${searchId}`;
  const drawerCoachmarkKey = 'webleads_results_drawer_coachmark:v1';
  const [showCoachmark, setShowCoachmark] = useState(false);
  const [showDrawerCoachmark, setShowDrawerCoachmark] = useState(false);
  const drawerTipDisplayedRef = useRef(false);

  useEffect(() => {
    drawerTipDisplayedRef.current = false;
  }, [searchId]);
  
  const { currentSearch, loading: searchLoading, error: searchError, fetchSearch } = useSearch();
  const hasSmartFilters = currentSearch?.website_filter?.enabled === true;
  const isNoWebsiteSearch = currentSearch?.google_maps_requirements?.has_website === false;
  const {
    places,
    summary,
    pagination,
    filters,
    statistics,
    verifiable_emails_count,
    enrichable_people_count,
    feature_access,
    auto_find_status,
    auto_find_mode,
    auto_find_success_limit,
    auto_find_success_count,
    auto_find_chargeable_count,
    auto_find_attempt_count,
    auto_find_no_email_count,
    auto_find_catch_all_count,
    auto_find_guardrail_reason,
    auto_find_business_target,
    auto_find_business_attempt_count,
    auto_find_max_people_per_business,
    auto_find_monthly_pool_limit,
    auto_find_monthly_pool_used,
    loading: resultsLoading,
    error: resultsError,
    fetchResults,
    fetchStatistics,
    setFilters,
    changePage,
    exportResults,
    clearError: clearResultsError,
  } = useResults();
  const effectiveFilters = normalizeSmartFilterParams(filters, hasSmartFilters);
  
  useEffect(() => {
    // Fetch current subscription plan
    const fetchCurrentPlan = async () => {
      try {
        const subscription = await subscriptionsApi.getCurrentSubscription();
        setCurrentPlan(subscription.subscription?.plan_name || 'discover');
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
        setCurrentPlan('discover');
      }
    };
    
    if (isAuthenticated) {
      fetchCurrentPlan();
    }
  }, [isAuthenticated]);
  
  useEffect(() => {
    if (!isAuthenticated) return;
    balanceApi.getBalance().then(setBalance).catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!currentSearch?.website_filter || !summary) return;
    posthog?.capture('website_filter_results_displayed', {
      search_id: searchId,
      website_confirmed_count: summary.website_confirmed_count,
      additional_category_count: summary.additional_category_count,
    });
  }, [currentSearch?.website_filter, posthog, searchId, summary]);

  useEffect(() => {
    // Only fetch if authenticated and we haven't initialized yet
    if (isAuthenticated && !hasInitialized.current && searchId > 0) {
      fetchSearch(searchId);
      fetchStatistics(searchId);
      fetchResults(searchId).then((result: any) => {
        const accessData = result?.payload?.access_info;
        if (accessData) {
          setAccessInfo(accessData);
        }
      });
      hasInitialized.current = true;
    }
  }, [searchId, fetchSearch, fetchResults, fetchStatistics, isAuthenticated]);

  useEffect(() => {
    if (summary && places.length > 0) {
      assertReadinessConsistency(summary, places);
    }
  }, [summary, places]);

  useEffect(() => {
    if (!summary || places.length === 0 || completedSearchViewedRef.current.has(searchId)) return;
    completedSearchViewedRef.current.add(searchId);
    posthog?.capture('completed_search_viewed', {
      search_id: searchId,
      current_plan: currentPlan,
      auto_find_mode,
      auto_find_status,
      success_limit: auto_find_success_limit,
      success_count: auto_find_success_count,
      attempt_count: auto_find_attempt_count,
      no_email_count: auto_find_no_email_count,
      catch_all_count: auto_find_catch_all_count,
      gate_reason: auto_find_guardrail_reason,
      purchased_dm_spent: 0,
      surface: 'results_page',
    });
  }, [
    auto_find_attempt_count,
    auto_find_catch_all_count,
    auto_find_guardrail_reason,
    auto_find_mode,
    auto_find_no_email_count,
    auto_find_status,
    auto_find_success_count,
    auto_find_success_limit,
    currentPlan,
    places.length,
    posthog,
    searchId,
    summary,
  ]);

  useEffect(() => {
    if (!accessInfo?.results_capped) return;
    const impressionKey = `${searchId}-${accessInfo.visible_results_cap ?? 'unknown'}`;
    if (resultsLimitViewedRef.current.has(impressionKey)) return;
    resultsLimitViewedRef.current.add(impressionKey);
    posthog?.capture('results_plan_limit_viewed', {
      context: 'results_lock',
      current_plan: currentPlan,
      daily_searches: null,
      max_results: accessInfo.visible_results_cap,
      max_queued_searches: null,
      recommended_plan: null,
      recommended_limit: null,
      active_searches: null,
      locked_places_count: Math.max(0, accessInfo.places_locked),
      visible_results_cap: accessInfo.visible_results_cap,
      search_id: searchId,
    });
  }, [
    accessInfo,
    auto_find_attempt_count,
    auto_find_mode,
    auto_find_no_email_count,
    auto_find_success_count,
    auto_find_success_limit,
    currentPlan,
    posthog,
    searchId,
  ]);

  useEffect(() => {
    if (!accessInfo?.results_capped) return;
    const lockedCount = Math.max(0, accessInfo.places_locked);
    if (lockedCount === 0) return;

    const ctaImpressionKey = `cta-${searchId}`;
    if (!resultsLimitViewedRef.current.has(ctaImpressionKey)) {
      resultsLimitViewedRef.current.add(ctaImpressionKey);
      posthog?.capture('upgrade_cta_shown', {
        search_id: searchId,
        current_plan: currentPlan,
        auto_find_mode,
        success_limit: auto_find_success_limit,
        success_count: auto_find_success_count,
        attempt_count: auto_find_attempt_count,
        no_email_count: auto_find_no_email_count,
        catch_all_count: auto_find_catch_all_count,
        gate_reason: 'ungated',
        locked_places_count: lockedCount,
        purchased_dm_spent: 0,
        surface: 'results_limit_banner',
      });
    }

  }, [
    accessInfo,
    auto_find_attempt_count,
    auto_find_catch_all_count,
    auto_find_mode,
    auto_find_no_email_count,
    auto_find_success_count,
    auto_find_success_limit,
    currentPlan,
    posthog,
    searchId,
  ]);

  useEffect(() => {
    if (localStorage.getItem(coachmarkKey) === 'dismissed') return;
    const hasUnlockable = places.some(place => findableCount(place) > 0);
    if (!hasUnlockable) return;
    const timer = setTimeout(() => setShowCoachmark(true), 800);
    return () => clearTimeout(timer);
  }, [places, coachmarkKey]);

  const scheduleDrawerCoachmarkAfterEnrich = () => {
    if (localStorage.getItem(drawerCoachmarkKey) === 'dismissed') return;
    if (places.length === 0) return;
    if (drawerTipDisplayedRef.current) return;
    window.setTimeout(() => {
      if (localStorage.getItem(drawerCoachmarkKey) === 'dismissed') return;
      if (drawerTipDisplayedRef.current) return;
      drawerTipDisplayedRef.current = true;
      posthog?.capture('drawer_coachmark_shown', { search_id: searchId });
      setShowDrawerCoachmark(true);
    }, 400);
  };

  const dismissDrawerCoachmark = (action: 'got_it' | 'opened_drawer') => {
    drawerTipDisplayedRef.current = true;
    setShowDrawerCoachmark(false);
    localStorage.setItem(drawerCoachmarkKey, 'dismissed');
    posthog?.capture('drawer_coachmark_dismissed', { search_id: searchId, action });
  };

  useEffect(() => {
    if (localStorage.getItem(drawerCoachmarkKey) === 'dismissed') return;
    if (places.length === 0) return;
    if (drawerTipDisplayedRef.current) return;

    const hasUnlockable = places.some(place => findableCount(place) > 0);
    const enrichCoachDismissed = localStorage.getItem(coachmarkKey) === 'dismissed';

    if (hasUnlockable && !enrichCoachDismissed) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (localStorage.getItem(drawerCoachmarkKey) === 'dismissed') return;
      if (drawerTipDisplayedRef.current) return;
      drawerTipDisplayedRef.current = true;
      posthog?.capture('drawer_coachmark_shown', { search_id: searchId });
      setShowDrawerCoachmark(true);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [places, coachmarkKey, searchId, posthog]);

  useEffect(() => {
    // Fetch industries for the whole search (not just the current page) from
    // the dedicated endpoint — the results row no longer carries `industries`.
    if (!searchId) return;
    let cancelled = false;
    getIndustries(searchId)
      .then(industries => {
        if (cancelled) return;
        setAvailableIndustries(Array.from(new Set(industries.map(i => i.name))).sort());
      })
      .catch(() => {
        if (!cancelled) setAvailableIndustries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [searchId]);

  const prevCompletionsCountRef = useRef(0);
  const dmUnlockFetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (recentCompletionsCount > prevCompletionsCountRef.current) {
      const bulkSearchId = activeBulkBatch?.searchId ?? null;
      if (bulkSearchId === null || bulkSearchId === searchId) {
        if (dmUnlockFetchDebounceRef.current) clearTimeout(dmUnlockFetchDebounceRef.current);
        dmUnlockFetchDebounceRef.current = setTimeout(() => {
          dispatch(fetchResultsThunk({
            searchId,
            page: pagination.page,
            limit: pagination.itemsPerPage,
            filters: normalizeSmartFilterParams(filters, hasSmartFilters),
            silent: true,
          }));
        }, 1500);
      }
    }
    prevCompletionsCountRef.current = recentCompletionsCount;
    return () => {
      if (dmUnlockFetchDebounceRef.current) clearTimeout(dmUnlockFetchDebounceRef.current);
    };
  }, [recentCompletionsCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const AUTO_FIND_POLL_INTERVAL_MS = 6000;
  useEffect(() => {
    const isAutoFindActive =
      (auto_find_mode === 'discover_auto' || auto_find_mode === 'paid_auto') &&
      (auto_find_status === 'PENDING' || auto_find_status === 'RUNNING');
    if (!isAutoFindActive) return;

    const interval = setInterval(() => {
      dispatch(fetchResultsThunk({
        searchId,
        page: pagination.page,
        limit: pagination.itemsPerPage,
        filters: normalizeSmartFilterParams(filters, hasSmartFilters),
        silent: true,
      }));
    }, AUTO_FIND_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [auto_find_mode, auto_find_status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) {
        setIsExportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleApplyFilters = (newFilters: FilterType) => {
    if (!isAuthenticated) {
      return;
    }
    const safeFilters = normalizeSmartFilterParams(newFilters, hasSmartFilters);
    setIsApplyingFilters(true);
    changePage(1);
    setFilters(safeFilters);
    fetchResults(searchId, 1, pagination.itemsPerPage, safeFilters)
      .then((action: any) => {
        if (action?.meta?.requestStatus === 'fulfilled' && safeFilters.googleAdsSignal) {
          posthog?.capture('google_ads_signal_filter_applied', {
            search_id: searchId,
            enabled: true,
            value: safeFilters.googleAdsSignal,
            returned_result_count: action.payload?.total ?? 0,
          });
        }
      })
      .finally(() => {
        setIsApplyingFilters(false);
      });
    setIsFilterDrawerOpen(false);
  };

  const handleRemoveFilter = (filterKey: keyof FilterType) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    const safeFilters = normalizeSmartFilterParams(newFilters, hasSmartFilters);
    setIsApplyingFilters(true);
    changePage(1);
    setFilters(safeFilters);
    fetchResults(searchId, 1, pagination.itemsPerPage, safeFilters)
      .then((action: any) => {
        if (action?.meta?.requestStatus === 'fulfilled' && filterKey === 'googleAdsSignal') {
          posthog?.capture('google_ads_signal_filter_applied', {
            search_id: searchId,
            enabled: false,
            value: 'any',
            returned_result_count: action.payload?.total ?? 0,
          });
        }
      })
      .finally(() => {
        setIsApplyingFilters(false);
      });
  };

  const handleClearAllFilters = () => {
    const clearedGoogleAdsSignal = filters.googleAdsSignal;
    setIsApplyingFilters(true);
    changePage(1);
    setFilters({});
    fetchResults(searchId, 1, pagination.itemsPerPage, {})
      .then((action: any) => {
        if (action?.meta?.requestStatus === 'fulfilled' && clearedGoogleAdsSignal) {
          posthog?.capture('google_ads_signal_filter_applied', {
            search_id: searchId,
            enabled: false,
            value: 'any',
            returned_result_count: action.payload?.total ?? 0,
          });
        }
      })
      .finally(() => {
        setIsApplyingFilters(false);
      });
  };
  
  const handlePageChange = (page: number) => {
    if (!isAuthenticated) return;
    if (page === pagination.page && !resultsError) return;
    if (resultsLoading) return;

    const startIndex = (page - 1) * pagination.itemsPerPage;
    const placesUnlocked = pagination.totalItems - (pagination.places_locked || 0);

    if (startIndex >= placesUnlocked && pagination.has_locked_results) {
      setIsLockedPageAccess(true);
      setShowUpgradeModal(true);
      return;
    }
    latestRequestedPageRef.current = page;
    setIsSwitchingResultsPage(true);
    changePage(page);
    fetchResults(searchId, page, pagination.itemsPerPage, effectiveFilters).finally(() => {
      if (latestRequestedPageRef.current === page) {
        setIsSwitchingResultsPage(false);
      }
    });
  };
  
  const handleExportClick = (format: 'csv' | 'excel') => {
    if (!isAuthenticated) return;
    if (accessInfo && accessInfo.has_locked_results && accessInfo.places_locked > 0) {
      setIsLockedPageAccess(false);
      setShowUpgradeModal(true);
      return;
    }
    setExportFormat(format);
    setIsExportModalOpen(true);
  };

  const handleExportConfirm = async (
    format: 'csv' | 'excel',
    selection: ExportSelection,
  ) => {
    await exportResults(
      searchId,
      format,
      selection.coverage,
      effectiveFilters,
      selection.includeSharedInboxes,
    );
    setHasExportedThisSearch(true);
    const exportedAt = new Date().toISOString();
    posthog?.capture('results_exported', {
      search_id: searchId,
      format,
      coverage: selection.coverage,
      include_shared_inboxes: selection.includeSharedInboxes,
      included_email_count: selection.includedEmailCount,
      filtered_lead_count: selection.filteredLeadCount,
      ready_decision_maker_email_count: selection.readyDecisionMakerEmailCount,
      ready_other_direct_email_count: selection.readyOtherDirectEmailCount,
      shared_inbox_count: selection.sharedInboxCount,
      unverified_direct_email_count: selection.unverifiedDirectEmailCount,
      has_filters: hasActiveFilters(effectiveFilters),
      google_ads_signal_filter: effectiveFilters.googleAdsSignal ?? 'any',
      current_plan: currentPlan,
      auto_find_mode,
      success_limit: auto_find_success_limit,
      success_count: auto_find_success_count,
      attempt_count: auto_find_attempt_count,
      no_email_count: auto_find_no_email_count,
      catch_all_count: auto_find_catch_all_count,
      purchased_dm_spent: 0,
      surface: 'results_page',
      total_results: pagination.totalItems,
      exported_at: exportedAt,
      $set: {
        has_exported_results: true,
        last_results_exported_at: exportedAt,
      },
    });
  };

  const handleExportNudgeVerify = () => {
    if (!(feature_access?.email_verification ?? true)) {
      setShowUpgradeModal(true);
      return;
    }
    setIsExportModalOpen(false);
    setVerifyOpenScope('all');
    setIsVerificationModalOpen(true);
  };

  const handleExportNudgeEnrich = () => {
    if (!hasDMUnlockAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setIsExportModalOpen(false);
    setEnrichOpenScope('all');
    setIsDMUnlockModalOpen(true);
  };

  const ensureUserDiscoverSummary = async (): Promise<void> => {
    if (userDiscoverSummary || userDiscoverSummaryLoading || !isAuthenticated) return;
    setUserDiscoverSummaryLoading(true);
    try {
      const summary = await getUserSummary();
      setUserDiscoverSummary(summary);
    } catch (error) {
      console.error('Failed to fetch user summary:', error);
      setUserDiscoverSummary(null);
    } finally {
      setUserDiscoverSummaryLoading(false);
    }
  };

  const openUpgradeModalForExhaustion = () => {
    setIsLockedPageAccess(false);
    setUpgradeModalContext('dm_unlock_exhausted');
    setShowUpgradeModal(true);
    void ensureUserDiscoverSummary();
  };

  const openExportFromFlow = () => {
    setIsVerificationModalOpen(false);
    setIsDMUnlockModalOpen(false);
    setExportFormat('csv');
    setIsExportModalOpen(true);
  };

  const openUpgradeModalForResultsLimit = () => {
    const lockedCount = Math.max(0, accessInfo?.places_locked ?? 0);
    posthog?.capture('upgrade_cta_clicked', {
      search_id: searchId,
      current_plan: currentPlan,
      auto_find_mode,
      success_limit: auto_find_success_limit,
      success_count: auto_find_success_count,
      attempt_count: auto_find_attempt_count,
      no_email_count: auto_find_no_email_count,
      catch_all_count: auto_find_catch_all_count,
      gate_reason: 'ungated',
      locked_places_count: lockedCount,
      purchased_dm_spent: 0,
      surface: 'results_limit_banner',
    });
    setIsLockedPageAccess(false);
    setUpgradeModalContext('results_lock');
    setShowUpgradeModal(true);
  };
  
  // Show a simpler message if not authenticated yet
  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Card>
          <div className="flex justify-center items-center py-12">
            <span className="text-gray-600">Please log in to view search results</span>
          </div>
        </Card>
      </PageContainer>
    );
  }
  
  if (searchLoading && !currentSearch) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center py-12">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">Loading search details...</span>
        </div>
      </PageContainer>
    );
  }
  
  if (!currentSearch) {
    return (
      <PageContainer>
        <Card>
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">Search not found</h3>
            <p className="mt-1 text-gray-500">The search you're looking for doesn't exist or you don't have access to it.</p>
            <div className="mt-6">
              <Link to="/searches">
                <Button>Back to Searches</Button>
              </Link>
            </div>
          </div>
        </Card>
      </PageContainer>
    );
  }
  
  const openFiltersDrawer = () => {
    setCompanyDrawerCloseSignal((prev) => prev + 1);
    setIsCompanyDrawerOpen(false);
    setIsFilterDrawerOpen(true);
  };
  
  const closeFiltersDrawer = () => {
    setIsFilterDrawerOpen(false);
  };
  
  // Collect all verifiable emails from places for bulk verification using the shared filter
  const nonCatchAllEmails = Array.from(
    new Map(
      places
        .flatMap(place => {
          const personEmails = place.people?.flatMap(person => person.emails || []) || [];
          const companyEmails = place.company_emails || [];
          return [...personEmails, ...companyEmails].filter(email => !email.is_catch_all);
        })
        .map(email => [`${email.source ?? 'person'}:${email.id}`, email] as const),
    ).values(),
  );

  // Use the shared isVerifiable helper for consistent filtering with backend
  const verifiableNonCatchAllEmails = nonCatchAllEmails.filter(isVerifiable);
  const currentPagePersonEmails = verifiableNonCatchAllEmails.filter(e => e.source !== 'place_data');
  const currentPagePlaceEmails  = verifiableNonCatchAllEmails.filter(e => e.source === 'place_data');

  // Collect person IDs from non-catch-all businesses only
  // People enrichment uses the business website domain for email discovery;
  // if that domain is catch-all, new emails will also be catch-all
  const unlockablePersonIdsFromNonCatchAll = places.flatMap(findablePeopleIds);

  return (
    <PageContainer title={`Results: ${currentSearch.search_query}`}>
      {/* Results are no longer credit-locked */}
      
      <div className="mb-4">
        <div className="flex items-center gap-1.5 text-[11px] font-medium mb-3 text-gray-400">
          <Link to="/searches" className="hover:text-[#8A3033] transition-colors duration-200">
            Searches
          </Link>
          <span>/</span>
          <Link to={`/searches/${searchId}`} className="hover:text-[#8A3033] transition-colors duration-200">
            {currentSearch.search_query}
          </Link>
          <span>/</span>
          <span className="text-gray-500">Results</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#8A3033] leading-tight tracking-tight">
              {currentSearch.search_query}
            </h1>
            <p className={`mt-1 text-xs ${isNoWebsiteSearch ? 'text-[#6B7280]' : 'text-[#9CA3AF]'}`}>
              {isNoWebsiteSearch
                ? 'Businesses with no known website — a focused prospect list for website services.'
                : 'Businesses and the decision-makers who run them — find their emails to start the conversation.'}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={openFiltersDrawer}
            className="text-xs px-2 py-1 sm:text-sm sm:px-3 sm:py-1.5 flex items-center transition-all duration-200 ease-in-out active:scale-95 flex-shrink-0"
          >
            <SlidersHorizontal className="h-4 w-4 mr-1 sm:mr-2" />
            <span>Filters</span>
            {countActiveFilters(effectiveFilters) > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#8A3033] text-white text-[10px] font-bold">
                {countActiveFilters(effectiveFilters)}
              </span>
            )}
          </Button>
        </div>
      </div>

      {(searchError || resultsError) && (
        <Notification
          type="error"
          message={(typeof searchError === 'string' ? searchError : (searchError as any)?.message) || resultsError || ''}
          onClose={clearResultsError}
        />
      )}
      
<HeroBar
        summary={summary}
        accessInfo={accessInfo}
        balance={balance}
        searchId={searchId}
        currentPlan={currentPlan}
        unlockableCount={enrichable_people_count}
        verifiableCount={verifiable_emails_count}
        autoFindStatus={auto_find_status}
        autoFindMode={auto_find_mode}
        autoFindSuccessLimit={auto_find_success_limit}
        autoFindSuccessCount={auto_find_success_count}
        autoFindChargeableCount={auto_find_chargeable_count}
        autoFindAttemptCount={auto_find_attempt_count}
        autoFindNoEmailCount={auto_find_no_email_count}
        autoFindCatchAllCount={auto_find_catch_all_count}
        autoFindGuardrailReason={auto_find_guardrail_reason}
        autoFindBusinessTarget={auto_find_business_target}
        autoFindBusinessAttemptCount={auto_find_business_attempt_count}
        autoFindMaxPeoplePerBusiness={auto_find_max_people_per_business}
        autoFindMonthlyPoolLimit={auto_find_monthly_pool_limit}
        autoFindMonthlyPoolUsed={auto_find_monthly_pool_used}
        isNoWebsiteSearch={isNoWebsiteSearch}
        places={places}
        loading={resultsLoading}
        onFindOwners={() => {
          if (!hasDMUnlockAccess) {
            setShowUpgradeModal(true);
            return;
          }
          setEnrichOpenScope('all');
          setIsDMUnlockModalOpen(true);
        }}
        onVerifyEmails={() => {
          if (!(feature_access?.email_verification ?? true)) {
            setShowUpgradeModal(true);
            return;
          }
          setVerifyOpenScope('all');
          setIsVerificationModalOpen(true);
        }}
        onExport={handleExportClick}
        onUpgradeClick={openUpgradeModalForExhaustion}
        onResultsLimitUpgradeClick={openUpgradeModalForResultsLimit}
      />

      {headEnrichmentCompletion && (
        <EnrichmentCompletionBanner
          completion={headEnrichmentCompletion}
          onVerifyClick={() => {
            setVerifyOpenScope('all');
            setIsVerificationModalOpen(true);
          }}
          onExportClick={() => handleExportClick('csv')}
          onDismiss={() => dispatch(clearRecentCompletion(headEnrichmentCompletion.batch_id))}
        />
      )}

      {completedVerification && (
        <VerificationCompletionBanner
          completion={completedVerification}
          onExportClick={() => handleExportClick('csv')}
          onDismiss={() => dispatch(clearCompletedVerification())}
        />
      )}

      <div className="grid grid-cols-1 gap-6">
        <div>
          <ActiveFilterPills
            filters={effectiveFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
          />

          <div className="relative">
            <TopProgressBar visible={isSwitchingResultsPage} />
            <ResultsTable
              places={places}
              pagination={pagination}
              onPageChange={handlePageChange}
              loading={resultsLoading}
              onExport={handleExportClick}
              onVerifyEmails={() => {
                if (!(feature_access?.email_verification ?? true)) {
                  setShowUpgradeModal(true);
                  return;
                }
                setVerifyOpenScope('all');
                setIsVerificationModalOpen(true);
              }}
              verifiableCount={verifiable_emails_count}
              websiteConfirmedCount={summary?.website_confirmed_count}
              googleAdsSignal={currentSearch?.google_ads_signal}
              googleAdsDetectedCount={summary?.google_ads_detected_count}
              balance={balance}
              onUpgradeClick={openUpgradeModalForExhaustion}
              currentPlan={currentPlan}
              autoFindMode={auto_find_mode}
              autoFindSuccessLimit={auto_find_success_limit}
              autoFindSuccessCount={auto_find_success_count}
              autoFindAttemptCount={auto_find_attempt_count}
              autoFindNoEmailCount={auto_find_no_email_count}
              autoFindCatchAllCount={auto_find_catch_all_count}
              autoFindGuardrailReason={auto_find_guardrail_reason}
              onCompanyDrawerStateChange={(open) => {
                setIsCompanyDrawerOpen(open);
                if (open) {
                  closeFiltersDrawer();
                  if (showDrawerCoachmark) {
                    dismissDrawerCoachmark('opened_drawer');
                  }
                }
              }}
              closeDrawerSignal={companyDrawerCloseSignal}
              searchId={searchId}
              showDrawerCoachmark={showDrawerCoachmark}
              onDismissDrawerCoachmarkGotIt={() => dismissDrawerCoachmark('got_it')}
              shortLabels={currentSearch?.website_filter?.offering_tags ?? {}}
              showWebsiteMatch={currentSearch?.website_filter?.enabled === true}
              isNoWebsiteSearch={isNoWebsiteSearch}
            />
          </div>
        </div>
      </div>
      
      {accessInfo && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setIsLockedPageAccess(false);
            setUpgradeModalContext('results_lock');
          }}
          lockedPlacesCount={accessInfo.total_places_found - accessInfo.places_unlocked}
          creditsNeeded={accessInfo.total_places_found - accessInfo.places_unlocked}
          currentPlan={currentPlan}
          isLockedPageAccess={isLockedPageAccess}
          context={isLockedPageAccess ? 'locked_page' : upgradeModalContext}
          visibleResultsCap={accessInfo.visible_results_cap}
          searchId={searchId}
          dmUnlocksUsed={summary?.ready_personal_count ?? summary?.dm_unlocked ?? 0}
          allTimeDecisionMakerEmailsFound={
            upgradeModalContext === 'dm_unlock_exhausted'
              ? (userDiscoverSummaryLoading ? null : userDiscoverSummary?.emails ?? 0)
              : null
          }
          recommendedPlanName={upgradeModalContext === 'dm_unlock_exhausted' ? 'starter' : undefined}
          autoFindMode={auto_find_mode}
          autoFindSuccessLimit={auto_find_success_limit}
          autoFindSuccessCount={auto_find_success_count}
          autoFindAttemptCount={auto_find_attempt_count}
          autoFindNoEmailCount={auto_find_no_email_count}
          autoFindCatchAllCount={auto_find_catch_all_count}
          autoFindGuardrailReason={auto_find_guardrail_reason}
        />
      )}

      <BulkDMUnlockModal
        isOpen={isDMUnlockModalOpen}
        onClose={() => { setIsDMUnlockModalOpen(false); setEnrichOpenScope('page'); }}
        personIds={unlockablePersonIdsFromNonCatchAll}
        searchId={searchId}
        filters={effectiveFilters}
        showCompanySelect={true}
        defaultScope={enrichOpenScope}
        totalFilteredCount={pagination.totalItems}
        onSuccess={() => {
          setHasSuccessfulEnrichment(true);
          balanceApi.getBalance().then(setBalance).catch(() => {});
          fetchResults(searchId);
        }}
        onUpgradeClick={openUpgradeModalForExhaustion}
      />

      {isVerificationModalOpen && (
        <EmailVerificationModal
          isOpen
          onClose={() => setIsVerificationModalOpen(false)}
          emailType="both"
          searchId={searchId}
          bulkMode={true}
          defaultScope={verifyOpenScope}
          personEmails={currentPagePersonEmails}
          placeEmails={currentPagePlaceEmails}
          onExportClick={() => handleExportClick('csv')}
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
      )}

      <ExportEmailConfidenceModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        searchId={searchId}
        format={exportFormat}
        filters={effectiveFilters}
        filteredLeadCount={pagination.totalItems}
        onExport={handleExportConfirm}
        onVerifyClick={handleExportNudgeVerify}
        onEnrichClick={handleExportNudgeEnrich}
        summary={summary}
        verifiableCount={verifiable_emails_count}
        enrichableCount={enrichable_people_count}
      />

      {isApplyingFilters && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <TopProgressBar visible={true} />
        </div>
      )}

      {/* Filter Drawer (mirrors company drawer animation) */}
      <>
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${
            isFilterDrawerOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeFiltersDrawer}
          aria-hidden="true"
        />

        <div
          className={`fixed inset-y-0 right-0 z-50 w-full sm:w-[95%] md:w-[432px] bg-white shadow-lg rounded-l-2xl transform transition-all duration-200 ease-in-out ${
            isFilterDrawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="filters-drawer-title"
        >
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-5 md:px-6 py-3 border-b border-[#F9F5F5] bg-white rounded-tl-2xl">
              <h2 id="filters-drawer-title" className="text-base sm:text-lg md:text-xl font-bold text-[#2D3142] tracking-tight truncate pr-4">
                Filters
              </h2>
              <button
                onClick={closeFiltersDrawer}
                className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 ease-in-out active:scale-95 flex-shrink-0"
                aria-label="Close filters drawer"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-2 sm:px-3 md:px-4 py-4">
              <ResultsFilter
                currentFilters={effectiveFilters}
                onApplyFilters={handleApplyFilters}
                availableIndustries={availableIndustries}
                disabled={pagination.all_locked || false}
                resultsCount={pagination.totalItems}
                statistics={statistics}
                hasSmartFilters={hasSmartFilters}
              />
            </div>
          </div>
        </div>
      </>
    </PageContainer>
  );
};

export default Results; 
